/**
 * Authentication Storage Utilities
 * 
 * Centralized functions for managing authentication tokens in localStorage.
 * These functions are used by both AuthContext and fetchWrapper to ensure
 * consistent token management across the application.
 */

import type { User } from './api';

const AUTH_STORAGE_KEY = 'sparkapp_auth';
const ANON_ID_KEY = 'sparkapp_anon_id';

export interface StoredAuth {
  user: User;
  tokens: {
    access: string;
    refresh: string;
  };
  hasCompletedAssessment: boolean;
}

/**
 * Get the current access token from localStorage
 * @returns Access token string or null if not found
 */
export function getAccessToken(): string | null {
  try {
    const authData = localStorage.getItem(AUTH_STORAGE_KEY);
    if (authData) {
      const parsed: StoredAuth = JSON.parse(authData);
      return parsed.tokens?.access || null;
    }
  } catch (error) {
    console.error('Failed to get access token:', error);
  }
  return null;
}

/**
 * Get the current refresh token from localStorage
 * @returns Refresh token string or null if not found
 */
export function getRefreshToken(): string | null {
  try {
    const authData = localStorage.getItem(AUTH_STORAGE_KEY);
    if (authData) {
      const parsed: StoredAuth = JSON.parse(authData);
      return parsed.tokens?.refresh || null;
    }
  } catch (error) {
    console.error('Failed to get refresh token:', error);
  }
  return null;
}

/**
 * Get the stored authentication data
 * @returns Stored auth data or null if not found
 */
export function getStoredAuth(): StoredAuth | null {
  try {
    const authData = localStorage.getItem(AUTH_STORAGE_KEY);
    if (authData) {
      return JSON.parse(authData);
    }
  } catch (error) {
    console.error('Failed to get stored auth:', error);
  }
  return null;
}

/**
 * Update only the access token while preserving user and refresh token
 * @param newAccessToken - New access token to store
 */
export function updateAccessToken(newAccessToken: string): void {
  try {
    const authData = localStorage.getItem(AUTH_STORAGE_KEY);
    if (authData) {
      const parsed: StoredAuth = JSON.parse(authData);
      parsed.tokens.access = newAccessToken;
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(parsed));
      
      // Dispatch custom event to notify AuthContext of token update
      window.dispatchEvent(new CustomEvent('auth-token-updated', { 
        detail: { tokens: parsed.tokens } 
      }));
    }
  } catch (error) {
    console.error('Failed to update access token:', error);
  }
}

/**
 * Update both access and refresh tokens while preserving user data
 * Used when backend implements refresh token rotation
 * @param newAccessToken - New access token to store
 * @param newRefreshToken - New refresh token to store
 */
export function updateBothTokens(newAccessToken: string, newRefreshToken: string): void {
  try {
    const authData = localStorage.getItem(AUTH_STORAGE_KEY);
    if (authData) {
      const parsed: StoredAuth = JSON.parse(authData);
      parsed.tokens.access = newAccessToken;
      parsed.tokens.refresh = newRefreshToken;
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(parsed));
      
      // Dispatch custom event to notify AuthContext of token update
      window.dispatchEvent(new CustomEvent('auth-token-updated', { 
        detail: { tokens: parsed.tokens } 
      }));
      
      console.log('[authStorage] Both access and refresh tokens updated');
    }
  } catch (error) {
    console.error('Failed to update both tokens:', error);
  }
}

/**
 * Store complete authentication data (user + tokens)
 * @param auth - Complete auth data to store
 */
export function setStoredAuth(auth: StoredAuth): void {
  try {
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(auth));
  } catch (error) {
    console.error('Failed to store auth data:', error);
  }
}

/**
 * Get or create a persistent anonymous ID for guest session reuse.
 * Stored in a separate localStorage key from auth tokens so it survives clearAuth().
 * Uses crypto.randomUUID() for UUID v4 generation.
 */
export function getOrCreateAnonymousId(): string {
  try {
    const existing = localStorage.getItem(ANON_ID_KEY);
    if (existing) {
      return existing;
    }
    const newId = crypto.randomUUID();
    localStorage.setItem(ANON_ID_KEY, newId);
    return newId;
  } catch (error) {
    // Fallback if localStorage is unavailable — generate but don't persist
    console.error('Failed to access localStorage for anonymous ID:', error);
    return crypto.randomUUID();
  }
}

/**
 * Clear all authentication data from storage
 * This is called when refresh fails or user logs out
 */
export function clearAuth(): void {
  try {
    localStorage.removeItem(AUTH_STORAGE_KEY);

    // IMPORTANT: Never clear sparkapp_anon_id here.
    // The anonymous ID is the last line of defense for guest session recovery.
    // It must survive auth clearing to allow session reuse on next login.

    // Dispatch event to notify AuthContext
    window.dispatchEvent(new Event('auth-cleared'));
  } catch (error) {
    console.error('Failed to clear auth data:', error);
  }
}
